import { useQueryClient } from "@tanstack/react-query";
import * as Sentry from "@sentry/react";
import { useNavigate } from "@tanstack/react-router";
import { supabase } from "../../lib/supabase";
import { queryKeys } from "../../lib/queryKeys";
import { showUndoToast, showTaskLinkToast } from "../../lib/undoStack";
import { Task } from "../../types";

async function patchTaskInDb(
  id: string,
  fields: Record<string, unknown>,
): Promise<boolean> {
  const { error } = await supabase
    .from("tasks")
    .update({ ...fields, updated_at: new Date().toISOString() })
    .eq("id", id);
  return !error;
}

export function useArchiveTask() {
  const queryClient = useQueryClient();
  const navigate = useNavigate();

  return {
    archiveTask: async (id: string): Promise<boolean> => {
      const tasks = queryClient.getQueryData<Task[]>(queryKeys.tasks) ?? [];
      const task = tasks.find((t) => t.id === id);
      if (!task) return false;

      const goToTask = () =>
        navigate({
          to: "/task/$taskId",
          params: { taskId: id },
          search: (prev: Record<string, unknown>) => prev,
        });

      const isArchivingNow = !task.archived;

      // Optimistic update.
      queryClient.setQueryData<Task[]>(queryKeys.tasks, (prev) =>
        (prev ?? []).map((t) =>
          t.id === id ? { ...t, archived: isArchivingNow } : t,
        ),
      );

      const ok = await patchTaskInDb(id, { archived: isArchivingNow });
      if (!ok) {
        // Roll back.
        queryClient.setQueryData<Task[]>(queryKeys.tasks, (prev) =>
          (prev ?? []).map((t) =>
            t.id === id ? { ...t, archived: task.archived } : t,
          ),
        );
        Sentry.captureException(new Error("archiveTask DB patch failed"), {
          extra: { mutation: "useArchiveTask", taskId: id },
        });
        showTaskLinkToast({
          prefix: `Failed to ${isArchivingNow ? "archive" : "restore"} `,
          label: `"${task.title}"`,
          labelSuffix: ". Please try again.",
          type: "error",
          onLabelClick: goToTask,
        });
        return false;
      }

      Sentry.addBreadcrumb({
        category: "task",
        message: isArchivingNow ? "Task archived" : "Task unarchived",
        data: { taskId: id },
        level: "info",
      });

      showUndoToast({
        label: `"${task.title}"`,
        labelSuffix: isArchivingNow ? " archived" : " restored",
        onLabelClick: goToTask,
        onUndo: async () => {
          const reverted = await patchTaskInDb(id, {
            archived: !isArchivingNow,
          });
          if (reverted) {
            queryClient.setQueryData<Task[]>(queryKeys.tasks, (prev) =>
              (prev ?? []).map((t) =>
                t.id === id ? { ...t, archived: !isArchivingNow } : t,
              ),
            );
          }
          queryClient.invalidateQueries({ queryKey: queryKeys.tasks });
        },
      });

      queryClient.invalidateQueries({ queryKey: queryKeys.tasks });
      return true;
    },
  };
}
